<template>
  <v-dialog v-bind:value="open" @click:outside="cancel" max-width="900px" scrollable persistent>
    <v-card>
      <v-card-title>
        <span class="headline">Macheo automático de cuentas</span>
      </v-card-title>

      <v-card-text>
        <v-alert dense text type="info" class="caption mb-4">
          Se proponen cuentas contables por similitud de nombre para los ítems sin mapear.
          Revisá y destildá lo que no corresponda antes de aplicar.
        </v-alert>

        <div v-if="hasSuggestions" class="d-flex mb-3">
          <v-btn text x-small @click="toggleAll(true)">
            <v-icon left x-small>done_all</v-icon> Marcar todo
          </v-btn>
          <v-btn text x-small @click="toggleAll(false)">
            <v-icon left x-small>remove_done</v-icon> Desmarcar todo
          </v-btn>
        </div>

        <div v-if="!hasSuggestions" class="caption grey--text text-center py-8">
          No se encontraron coincidencias automáticas.
        </div>

        <template v-else>
          <div v-if="money.length" class="mb-5">
            <div class="subtitle-2 font-weight-bold mb-1">Cuentas de tesorería</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th style="width: 40px">
                      <v-checkbox v-model="allMoney" hide-details dense class="ma-0 pa-0"></v-checkbox>
                    </th>
                    <th class="text-left">Cuenta de tesorería</th>
                    <th class="text-left">Cuenta contable propuesta</th>
                    <th class="text-right" style="width: 80px">Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in money" :key="'m-' + m.id">
                    <td><v-checkbox v-model="m.selected" hide-details dense class="ma-0 pa-0"></v-checkbox></td>
                    <td>{{ m.name }}</td>
                    <td>{{ m.account.label }}</td>
                    <td class="text-right" :class="scoreClass(m.score)">{{ m.score }}%</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <div v-if="conceptos.length" class="mb-5">
            <div class="subtitle-2 font-weight-bold mb-1">Conceptos</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Concepto</th>
                    <th style="width: 40px">
                      <v-checkbox v-model="allConceptoVenta" hide-details dense class="ma-0 pa-0"></v-checkbox>
                    </th>
                    <th class="text-left">Venta (Ingresos)</th>
                    <th style="width: 40px">
                      <v-checkbox v-model="allConceptoCompra" hide-details dense class="ma-0 pa-0"></v-checkbox>
                    </th>
                    <th class="text-left">Compra (Egresos)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in conceptos" :key="'c-' + c.id">
                    <td>{{ c.name }}</td>
                    <td>
                      <v-checkbox v-if="c.account_venta" v-model="c.selected_venta" hide-details dense class="ma-0 pa-0"></v-checkbox>
                    </td>
                    <td :class="c.account_venta ? scoreClass(c.score_venta) : ''">
                      <template v-if="c.account_venta">{{ c.account_venta.label }} ({{ c.score_venta }}%)</template>
                      <span v-else class="grey--text">—</span>
                    </td>
                    <td>
                      <v-checkbox v-if="c.account_compra" v-model="c.selected_compra" hide-details dense class="ma-0 pa-0"></v-checkbox>
                    </td>
                    <td :class="c.account_compra ? scoreClass(c.score_compra) : ''">
                      <template v-if="c.account_compra">{{ c.account_compra.label }} ({{ c.score_compra }}%)</template>
                      <span v-else class="grey--text">—</span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

          <div v-if="defaults.length">
            <div class="subtitle-2 font-weight-bold mb-1">Cuentas de sistema</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th style="width: 40px">
                      <v-checkbox v-model="allDefaults" hide-details dense class="ma-0 pa-0"></v-checkbox>
                    </th>
                    <th class="text-left">Cuenta de sistema</th>
                    <th class="text-left">Cuenta contable propuesta</th>
                    <th class="text-right" style="width: 80px">Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in defaults" :key="'d-' + d.key">
                    <td><v-checkbox v-model="d.selected" hide-details dense class="ma-0 pa-0"></v-checkbox></td>
                    <td>{{ defaultName(d.key) }}</td>
                    <td>{{ d.account.label }}</td>
                    <td class="text-right" :class="scoreClass(d.score)">{{ d.score }}%</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">{{ hasSuggestions ? "Cancelar" : "Cerrar" }}</v-btn>
        <v-btn
          v-if="hasSuggestions"
          color="success"
          :disabled="!hasSelection"
          :loading="applying"
          @click="aceptar"
        >
          Aplicar seleccionados
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const DEFAULT_NAMES = {
  DEUDORES_VENTAS: "Deudores por Ventas",
  ACREEDORES_COMPRAS: "Acreedores por Compras",
  IVA_DEBITO: "IVA Débito Fiscal",
  IVA_CREDITO: "IVA Crédito Fiscal",
  VENTAS: "Ventas",
  VENTAS_X: "Ventas X (no fiscal)",
  COMPRAS_GASTOS: "Compras / Gastos",
  VALORES_RECHAZADOS_TERCEROS: "Valores Rechazados (terceros)",
  VALORES_RECHAZADOS_PROPIOS: "Valores Rechazados (propios)",
  RETENCION_SUFRIDA: "Retención Sufrida",
  RETENCION_PRACTICADA: "Retención Practicada",
  PERCEPCION: "Percepción (practicada)",
  PERCEPCION_SUFRIDA: "Percepción Sufrida",
  RESULTADO_EJERCICIO: "Resultado del Ejercicio",
  RESULTADOS_ACUMULADOS: "Resultados Acumulados",
};

export default {
  props: {
    open: { type: Boolean, default: false },
    suggestions: { type: Object, default: null },
    applying: { type: Boolean, default: false },
  },
  data() {
    return {
      // Por debajo de este score la propuesta llega destildada: se sigue mostrando
      // (siempre el mejor candidato disponible) pero no se aplica sin revisión.
      minConfidentScore: 55,
      money: [],
      conceptos: [],
      defaults: [],
    };
  },
  computed: {
    hasSuggestions() {
      return this.money.length > 0 || this.conceptos.length > 0 || this.defaults.length > 0;
    },
    hasSelection() {
      return (
        this.money.some((m) => m.selected) ||
        this.conceptos.some((c) => c.selected_venta || c.selected_compra) ||
        this.defaults.some((d) => d.selected)
      );
    },
    allMoney: {
      get() {
        return this.money.length > 0 && this.money.every((m) => m.selected);
      },
      set(value) {
        this.money.forEach((m) => (m.selected = value));
      },
    },
    allConceptoVenta: {
      get() {
        const conVenta = this.conceptos.filter((c) => c.account_venta);
        return conVenta.length > 0 && conVenta.every((c) => c.selected_venta);
      },
      set(value) {
        this.conceptos.forEach((c) => {
          if (c.account_venta) c.selected_venta = value;
        });
      },
    },
    allConceptoCompra: {
      get() {
        const conCompra = this.conceptos.filter((c) => c.account_compra);
        return conCompra.length > 0 && conCompra.every((c) => c.selected_compra);
      },
      set(value) {
        this.conceptos.forEach((c) => {
          if (c.account_compra) c.selected_compra = value;
        });
      },
    },
    allDefaults: {
      get() {
        return this.defaults.length > 0 && this.defaults.every((d) => d.selected);
      },
      set(value) {
        this.defaults.forEach((d) => (d.selected = value));
      },
    },
  },
  watch: {
    open(value) {
      if (value) this.load();
    },
  },
  methods: {
    load() {
      const s = this.suggestions || {};
      this.money = (s.money_accounts || []).map((m) => ({ ...m, selected: m.score >= this.minConfidentScore }));
      this.conceptos = (s.conceptos || []).map((c) => ({
        ...c,
        selected_venta: !!c.account_venta && c.score_venta >= this.minConfidentScore,
        selected_compra: !!c.account_compra && c.score_compra >= this.minConfidentScore,
      }));
      this.defaults = (s.defaults || []).map((d) => ({ ...d, selected: d.score >= this.minConfidentScore }));
    },
    defaultName(key) {
      return DEFAULT_NAMES[key] || key;
    },
    scoreClass(score) {
      return score >= this.minConfidentScore ? "" : "orange--text text--darken-2";
    },
    toggleAll(value) {
      this.allMoney = value;
      this.allConceptoVenta = value;
      this.allConceptoCompra = value;
      this.allDefaults = value;
    },
    cancel() {
      this.$emit("cancel");
    },
    aceptar() {
      this.$emit("accept", {
        money_accounts: this.money.filter((m) => m.selected),
        conceptos: this.conceptos
          .filter((c) => c.selected_venta || c.selected_compra)
          .map((c) => ({
            id: c.id,
            account_venta_id: c.selected_venta && c.account_venta ? c.account_venta.id : null,
            account_compra_id: c.selected_compra && c.account_compra ? c.account_compra.id : null,
          })),
        defaults: this.defaults.filter((d) => d.selected),
      });
    },
  },
};
</script>
